import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import i18n from 'i18next';
import _ from 'lodash';
import CommonAssetHeader from '../../CommonAssetHeader';
import RecordingMessagesTable from './RecordingMessagesTable';
import RecordingMessageModal from './RecordingMessageModal';
import {
  deleteRecordingMessagesBulk,
  deleteRecordingMessagesById,
  fetchRecordingMessagesThunk,
  getSelectedRecordingMessagesLength,
  updateRecordingMessagesFilterPage,
} from '../../../../store/recordings/recordingMessages/recordingMessagesActions';
import { showToastNotification } from '../../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../../store/ui/toasts/toastsTypes';
import { confirm } from '../../../../components/ui/modal/BlazeConfirmationModal';
import { useIsMount } from '../../../../hooks/useIsMountHook';
import { getErrorMessage, getSelectedCountTitle } from '../../../../utils/miscUtil';
import { convertToHI } from '../../Transactions/TransactionEditor/TransactionEditor';
import { MessageType } from '../../../../types/rest/dslTypes';
import { ApplicationState } from '../../../../store/rootReducer';
import { Recording, RecordingMessage } from '../../../../types/rest/assetCatalogTypes';
import BzmIcon from '../../../../components/icons/BzmIcon';
import {
  setIsLoadingRecordingMessage,
  setSelectedRecordingMessages,
} from '../../../../store/recordings/recordingMessages/recordingMessagesReducer';
import MessagesIndexModal from '../../../MockServices/Tabs/MockServices/Modals/MessagesIndexModal';
import { SelectAllType } from '../../../../components/blazeTable/BlazeTable.types';

interface RecordingEditorBodyProps {
  recording: Recording;
  isCompact?: boolean;
}

const getEmptyRecordingMessage = (recordingId: number): RecordingMessage => ({
  id: -1,
  recordingId,
  name: '',
  messageType: MessageType.TEXT_MESSAGE,
  headers: [],
  properties: [],
  content: '',
  destination: '',
  destinationType: 'QUEUE',
  delay: 0,
  created: 0,
  updated: 0,
  recordedAt: 0,
  createdBy: '',
  updatedBy: '',
  updatedDate: '',
  createdDate: '',
});

export const getRecordingMessageWithFormat = (recMsg: RecordingMessage) => {
  return {
    ...recMsg,
    headers: convertToHI(recMsg.headers),
    properties: convertToHI(recMsg.properties),
    content: recMsg.content ? Buffer.from(recMsg.content, 'base64').toString() : '',
  };
};

const RecordingMessages = ({ recording, isCompact }: RecordingEditorBodyProps) => {
  const isMount = useIsMount();
  const dispatch = useDispatch();
  const recordingMessages = useSelector((state: ApplicationState) => state.recordingMessages);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [recordingMessageDetail, setRecordingMessageDetail] = useState<RecordingMessage | null>(null);
  const [showMessageIndexModal, setShowMessageIndexModal] = useState(false);

  useEffect(() => {
    if (isMount) {
      dispatch(fetchRecordingMessagesThunk(recording.id, false));
    }
  }, [dispatch, isMount]);

  const debouncedUpdateMessageFilter = useRef(
    _.debounce((searchTerm: string) => {
      dispatch(updateRecordingMessagesFilterPage(recording.id, searchTerm));
    }, 750),
  ).current;

  useEffect(() => {
    if (!isMount) {
      debouncedUpdateMessageFilter(searchKeyword);
    }
  }, [searchKeyword, debouncedUpdateMessageFilter, isMount]);

  const count = getSelectedCountTitle(recordingMessages.totalMessages, getSelectedRecordingMessagesLength());
  const clearTableSelection = () => setSelectedRecordingMessages({ selectedIds: [], selectAllType: null });
  const isDeleteDisabled = () => _.isEmpty(recordingMessages.selectedRecordingMessages);

  const selectRecordingMessagesHandler = (ids: number[], selectAllType?: SelectAllType) => {
    if (selectAllType === 'ALL') {
      dispatch(setSelectedRecordingMessages({ selectedIds: [-1], selectAllType: selectAllType }));
    } else {
      dispatch(setSelectedRecordingMessages({ selectedIds: ids, selectAllType: selectAllType }));
    }
  };

  const onDelete = (rMsg?: RecordingMessage) => {
    const modalBody = _.isNil(rMsg) ? (
      <div>
        {i18n.t('recordings:actions.messages.deleteBulk', {
          numberOfSelected: getSelectedRecordingMessagesLength(),
        })}
      </div>
    ) : (
      <div>
        {i18n.t('recordings:actions.messages.delete')} <strong className="text-break">{rMsg.name}</strong>?
      </div>
    );

    confirm({
      confirmation: modalBody,
      yesOptionTitle: i18n.t('common:actions.delete'),
      title: i18n.t('recordings:actions.messages.deleteTitle'),
    }).then(async () => {
      dispatch(setIsLoadingRecordingMessage(true));
      try {
        if (_.isNil(rMsg)) {
          await dispatch(deleteRecordingMessagesBulk(recording.id, recordingMessages.selectedRecordingMessages));
        } else {
          await dispatch(deleteRecordingMessagesById(recording.id, rMsg));
        }
        dispatch(fetchRecordingMessagesThunk(recording.id, false));
      } catch (err) {
        dispatch(
          showToastNotification(
            ToastType.Danger,
            i18n.t('recordings:errorMsgs.messages.failedToDelete'),
            getErrorMessage(err),
          ),
        );
      } finally {
        clearTableSelection();
      }
    });
  };

  const onCloseModal = () => {
    setRecordingMessageDetail(null);
  };

  const createNewRecordingMessageModal = () => {
    setRecordingMessageDetail(getEmptyRecordingMessage(recording.id));
  };

  const onOpenDetail = (recMsg: RecordingMessage) => {
    setRecordingMessageDetail(getRecordingMessageWithFormat(recMsg));
  };

  const getChangeOrderButton = () => (
    <Button
      disabled={recordingMessages.totalMessages < 2}
      className="float-right mr-4"
      variant="secondary"
      style={{ marginTop: '11px' }}
      onClick={() => setShowMessageIndexModal(true)}
    >
      <BzmIcon variant="icon-bzm-edit" />
      <span className="ml-2"> Change Order </span>
    </Button>
  );

  return (
    <div className="mt-3">
      {recordingMessageDetail && (
        <RecordingMessageModal recordingMessage={recordingMessageDetail} onClose={onCloseModal} />
      )}
      {showMessageIndexModal && (
        <MessagesIndexModal
          recordingId={recording.id}
          recordingName={recording.name}
          onHide={() => setShowMessageIndexModal(false)}
        />
      )}
      <CommonAssetHeader
        assetType="MESSAGE"
        handleCreateNewAsset={createNewRecordingMessageModal}
        searchKeyword={searchKeyword}
        selectedAssets={recordingMessages.selectedRecordingMessages}
        clearTableSelection={clearTableSelection}
        setSearchKeyword={setSearchKeyword}
        count={count}
        isDeleteDisabled={isDeleteDisabled}
        onDelete={onDelete}
        middleComponent={!isCompact && getChangeOrderButton()}
        isCompact={isCompact}
        showAddButton={!isCompact}
      />
      <RecordingMessagesTable
        recordingId={recording.id}
        isCreatingNewMessage={recordingMessages.isCreatingNewRecordingMessage}
        expandedRecordingMessageId={recordingMessages.expandedRecordingMessageId}
        isLoading={recordingMessages.isLoading}
        onOpenDetail={onOpenDetail}
        onSelectionChange={selectRecordingMessagesHandler}
        recordingMessages={recordingMessages.recordingMessages}
        onDelete={onDelete}
        selectedItems={recordingMessages.selectedRecordingMessages}
        recordingMessagePageable={recordingMessages.pageable}
        recordingMessageSort={recordingMessages.sort}
        selectAllType={recordingMessages.selectAllType}
        totalMessages={recordingMessages.totalMessages}
        isCompact={isCompact}
      />
    </div>
  );
};

export default RecordingMessages;
